import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Pressable,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList, Message, Conversation } from '../types';
import { AppHeader, ScreenLayout, MessageBubble, Input, ItemInfoPanel, PageBand } from '../components';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useResponsive } from '../utils/responsive';
import { Brand, Colors, ControlHeight, Fonts, Spacing, Typography, Radii } from '../constants';
import { conversationsService } from '../services/conversations';
import { messagesService } from '../services/messages';
import { authService } from '../services/auth';
import { useDialog } from '../contexts';
import { RealtimeChannel } from '@supabase/supabase-js';

type Props = NativeStackScreenProps<RootStackParamList, 'Chat'>;

/**
 * Composer sizing. The field starts one line high and grows with the text up
 * to `COMPOSER_MAX_LINES`; only then does it scroll. Padding is derived from
 * the control height so a single line sits centred (no 1px overflow, which on
 * browsers with classic scrollbars showed a permanent scrollbar in the field).
 *
 * Native auto-sizes a multiline input between `minHeight` and `maxHeight` on
 * its own; the web textarea does not, so there the height is measured on
 * every change (see `measureWebComposer`).
 */
const COMPOSER_LINE_HEIGHT = 22;
const COMPOSER_MAX_LINES = 5;
const COMPOSER_BORDER = 1; // the Input's hairline, inside the box height
const composerMetrics = (minHeight: number) => {
  const paddingVertical = (minHeight - COMPOSER_BORDER * 2 - COMPOSER_LINE_HEIGHT) / 2;
  return {
    minHeight,
    paddingVertical,
    maxHeight: COMPOSER_LINE_HEIGHT * COMPOSER_MAX_LINES + paddingVertical * 2 + COMPOSER_BORDER * 2,
  };
};

export const ChatScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { isDesktop } = useResponsive();
  const insets = useSafeAreaInsets();
  const { conversationId } = route.params;
  const isFocused = useIsFocused();

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);

  // Desktop gets the taller (52) control; the phone keeps the standard 48.
  const composer = composerMetrics(isDesktop ? ControlHeight.lg : ControlHeight.md);
  const [inputHeight, setInputHeight] = useState(composer.minHeight);
  const inputAtMax = inputHeight >= composer.maxHeight;

  const scrollViewRef = useRef<ScrollView>(null);
  const subscriptionRef = useRef<RealtimeChannel | null>(null);
  const isFocusedRef = useRef(isFocused);
  const currentUserIdRef = useRef<string | null>(null);
  const messageInputRef = useRef<TextInput>(null);

  // Web: size the textarea to its text. A textarea's scrollHeight never reports
  // less than its current height, so the field is collapsed to the minimum
  // first — otherwise it would grow but never shrink when lines are deleted.
  // scrollHeight is the padding box; the field's height also holds the border.
  const measureWebComposer = () => {
    if (Platform.OS !== 'web') return;
    const node = messageInputRef.current as unknown as HTMLTextAreaElement | null;
    if (!node?.style) return;
    node.style.height = `${composer.minHeight}px`;
    const next = Math.min(
      composer.maxHeight,
      Math.max(composer.minHeight, node.scrollHeight + COMPOSER_BORDER * 2)
    );
    node.style.height = `${next}px`;
    setInputHeight(next);
  };

  const handleChangeText = (text: string) => {
    setMessageText(text);
    measureWebComposer();
  };

  // Keep refs in sync with state
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    loadCurrentUser();
    loadConversation();
    loadMessages();

    // Set up real-time subscription for messages
    // Pass isFocusedRef.current to only mark as read when screen is focused
    const subscription = messagesService.subscribeToMessages(conversationId, (message) => {
      handleNewMessage(message, isFocusedRef.current);
    });
    subscriptionRef.current = subscription;

    // Mark messages as read when entering chat
    messagesService.markConversationAsRead(conversationId).catch(() => {
      // Silently fail - not critical
    });

    // Cleanup subscriptions on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [conversationId]);

  // Focus message input when screen loads
  useEffect(() => {
    if (!loading && conversation) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [loading, conversation]);

  // Refresh conversation data when screen regains focus
  // This ensures the UI reflects changes made elsewhere (e.g. item updated)
  const hasLoadedRef = useRef(false);

  useFocusEffect(
    React.useCallback(() => {
      // Skip the initial focus (handled by useEffect above)
      if (!hasLoadedRef.current) {
        hasLoadedRef.current = true;
        return;
      }

      // Refresh data when returning to this screen
      console.log('[ChatScreen] Screen focused, refreshing data...');
      loadConversation();
      loadMessages();
    }, [conversationId])
  );

  const loadCurrentUser = async () => {
    const user = await authService.getCurrentUser();
    setCurrentUserId(user?.id || null);
  };

  const loadConversation = async () => {
    try {
      const data = await conversationsService.getConversationById(conversationId);
      setConversation(data);
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), t('screens.chat.loadConversationError'), 'error');
      navigation.goBack();
    }
  };

  const loadMessages = async () => {
    try {
      const data = await messagesService.getMessages(conversationId);
      setMessages(data);
      setLoading(false);

      // Scroll to bottom after messages load
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: false });
      }, 100);
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), t('screens.chat.loadMessagesError'), 'error');
      setLoading(false);
    }
  };

  const handleNewMessage = (message: Message, markAsRead: boolean = true) => {
    setMessages((prev) => {
      // Check if message already exists (prevent duplicates)
      if (prev.some((m) => m.id === message.id)) {
        return prev;
      }
      return [...prev, message];
    });

    // Auto-scroll to bottom
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);

    // Only mark as read if:
    // 1. Screen is focused (markAsRead is true)
    // 2. currentUserId is loaded (not null)
    // 3. Message is not from current user
    // Use ref to get latest value (avoids stale closure issues)
    const userId = currentUserIdRef.current;
    if (markAsRead && userId && message.sender_id !== userId) {
      messagesService.markAsRead(message.id).catch(() => {
        // Silently fail - not critical
      });
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || sending || !currentUserId) return;

    const textToSend = messageText.trim();
    setMessageText(''); // Clear input immediately
    setInputHeight(composer.minHeight);
    setSending(true);

    try {
      const sentMessage = await messagesService.sendMessage(conversationId, textToSend);

      // Optimistically add message to local state for immediate feedback
      // Real-time subscription will also receive it, but duplicate check prevents duplication
      handleNewMessage(sentMessage);
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), t('screens.chat.sendMessageError'), 'error');
      setMessageText(textToSend); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: { nativeEvent: { key: string; metaKey?: boolean; ctrlKey?: boolean } }) => {
    // Send message with Cmd+Enter (macOS) or Ctrl+Enter (Windows/Linux)
    if (e.nativeEvent.key === 'Enter' && (e.nativeEvent.metaKey || e.nativeEvent.ctrlKey)) {
      handleSendMessage();
    }
  };

  const handleItemPress = () => {
    if (conversation?.item_id) {
      navigation.navigate('ItemDetails', { itemId: conversation.item_id });
    }
  };

  const handleClose = async () => {
    // Check if any messages were sent
    if (messages.length === 0) {
      // Show warning dialog
      const confirmed = await dialog.confirm(
        t('screens.chat.closeEmptyChatTitle'),
        t('screens.chat.closeEmptyChatMessage'),
        { variant: 'primary' }
      );

      if (confirmed) {
        // Delete the empty conversation
        try {
          console.log('[ChatScreen] Attempting to delete conversation:', conversationId);
          await conversationsService.deleteConversation(conversationId);
          console.log('[ChatScreen] Conversation deleted successfully');
        } catch (error) {
          // Silently fail - conversation might have been deleted already
          console.error('[ChatScreen] Failed to delete conversation:', error);
        }
        // Always navigate to ConversationsList for consistent behavior
        navigation.navigate('ConversationsList');
      }
      // If not confirmed, stay on the screen (do nothing)
    } else {
      // If messages exist, navigate to ConversationsList
      navigation.navigate('ConversationsList');
    }
  };

  // Desktop: the conversation lives under the same Bosgroen band as the
  // Messages list, so opening a chat only swaps the headline — the column
  // keeps its 800 width and the band its height (a skeleton holds the title
  // while the conversation loads).
  const bandKicker = t('screens.chat.conversationWith');
  // Back arrow left of the headline, as PageTitle does on the other detail pages.
  const renderBandBack = () => (
    <Pressable
      onPress={handleClose}
      style={({ hovered }: any) => [styles.bandBack, hovered && styles.bandBackHovered]}
      hitSlop={8}
      accessibilityRole="button"
      accessibilityLabel={t('common.buttons.back')}
    >
      <Ionicons name="arrow-back" size={24} color={Colors.white} />
    </Pressable>
  );

  if (loading || !conversation) {
    const loadingBand = (
      <PageBand
        kicker={bandKicker}
        title={
          <View style={styles.bandTitleRow}>
            {renderBandBack()}
            <View style={styles.titleSkeleton} />
          </View>
        }
      />
    );
    // Same surfaces as the loaded chat (white panel strip, linen messages
    // area), so opening a conversation doesn't flash a white column first.
    return (
      <ScreenLayout maxWidth={800} band={loadingBand} bandToolbar={false}>
        <View style={styles.container}>
          {!isDesktop && (
            <AppHeader
              navigation={{
                ...navigation,
                goBack: handleClose,
              }}
              currentScreen="Chat"
              title={t('screens.chat.title')}
              showBackButton={true}
              hideMenu={true}
            />
          )}
          <View style={styles.panelSkeleton} />
          <View style={[styles.centerContainer, styles.messagesContainer]}>
            <Text style={styles.loadingText}>{t('common.loading')}</Text>
          </View>
        </View>
      </ScreenLayout>
    );
  }

  const isBuyer = currentUserId === conversation.buyer_id;
  const otherUser = isBuyer ? conversation.seller : conversation.buyer;
  const otherUserName =
    [otherUser?.first_name, otherUser?.last_name].filter(Boolean).join(' ').trim() ||
    t('screens.chat.unknownMember');
  const isReserved = conversation.item?.status === 'reserved';

  const band = (
    <PageBand
      kicker={bandKicker}
      title={
        <View style={styles.bandTitleRow}>
          {renderBandBack()}
          <Text style={[Typography.title, styles.bandTitle]} numberOfLines={1}>
            {otherUserName}
          </Text>
        </View>
      }
      right={
        isReserved ? (
          <View style={styles.reservedChipOnDark}>
            <Text style={styles.reservedChipOnDarkText}>{t('enums.status.reserved')}</Text>
          </View>
        ) : undefined
      }
    />
  );

  return (
    <ScreenLayout maxWidth={800} band={band} bandToolbar={false}>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        {/* Mobile header; on desktop the name sits in the band */}
        {!isDesktop && (
          <AppHeader
            navigation={{
              ...navigation,
              goBack: handleClose,
            }}
            currentScreen="Chat"
            title={otherUserName}
            showBackButton={true}
            hideMenu={true}
            statusBadge={isReserved ? { label: t('enums.status.reserved'), variant: 'primary' } : undefined}
          />
        )}

        {/* Action Bar Container - always rendered for consistent shadow */}
        <View style={styles.actionBarContainer}>
          {/* Item Info Panel or Deleted Item Banner */}
          {conversation.item ? (
            <ItemInfoPanel
              item={conversation.item}
              onItemPress={handleItemPress}
              defaultExpanded={false}
            />
          ) : (
            <View style={styles.deletedItemBanner}>
              <Ionicons name="trash-outline" size={16} color={Colors.textLight} />
              <Text style={styles.deletedItemText}>
                {t('screens.chat.itemDeleted')}
              </Text>
            </View>
          )}

        </View>

        {/* Messages list */}
        <ScrollView
          ref={scrollViewRef}
          style={styles.messagesContainer}
          contentContainerStyle={styles.messagesContent}
          keyboardShouldPersistTaps="handled"
        >
          {messages.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>{t('screens.chat.noMessages')}</Text>
              <Text style={styles.emptyStateSubtext}>{t('screens.chat.noMessagesSubtext')}</Text>
            </View>
          ) : (
            messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwnMessage={message.sender_id === currentUserId}
                showSender={false}
              />
            ))
          )}
        </ScrollView>

        {/* Composer */}
        <View
          style={[
            styles.inputContainer,
            isDesktop ? styles.inputContainerDesktop : { paddingBottom: Math.max(insets.bottom, Spacing.md) },
          ]}
        >
          <Input
            ref={messageInputRef}
            value={messageText}
            onChangeText={handleChangeText}
            placeholder={t('screens.chat.typePlaceholder')}
            multiline
            maxLength={1000}
            style={styles.messageInputContainer}
            inputStyle={[
              styles.messageInput,
              {
                height: Platform.OS === 'web' ? inputHeight : undefined,
                minHeight: composer.minHeight,
                maxHeight: composer.maxHeight,
                paddingTop: composer.paddingVertical,
                paddingBottom: composer.paddingVertical,
              },
              // Scroll only once the field has grown to its last line.
              Platform.OS === 'web' && ({ overflowY: inputAtMax ? 'auto' : 'hidden' } as any),
            ]}
            onSubmitEditing={handleSendMessage}
            onKeyPress={handleKeyPress}
            blurOnSubmit={false}
          />
          <TouchableOpacity
            style={[
              styles.sendButton,
              { width: composer.minHeight, height: composer.minHeight },
              (!messageText.trim() || sending) && styles.sendButtonDisabled,
            ]}
            onPress={handleSendMessage}
            disabled={!messageText.trim() || sending}
            accessibilityRole="button"
            accessibilityLabel={t('screens.chat.send')}
            testID="chat-send"
          >
            <Ionicons name="arrow-up-outline" size={22} color={Colors.white} />
          </TouchableOpacity>
        </View>

      </KeyboardAvoidingView>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Loading: holds the collapsed ItemInfoPanel's height (10 + 44 + 10 + hairline)
  panelSkeleton: {
    height: 65,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  loadingText: {
    ...Typography.meta,
    color: Colors.textLight,
  },

  // Action bar container with shadow
  actionBarContainer: {
    backgroundColor: Colors.white,
    zIndex: 1, // Keep the panel above the messages area
  },

  // Band (desktop): back arrow + name (or a skeleton while loading), reserved chip right
  bandTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    minWidth: 0,
  },
  bandTitle: {
    color: Colors.white,
    flexShrink: 1,
  },
  titleSkeleton: {
    width: 240,
    height: Typography.title.lineHeight,
    borderRadius: Radii.tag,
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
  },
  reservedChipOnDark: {
    marginBottom: 6, // sits on the title's baseline rather than its descender line
    backgroundColor: Brand.saliemist,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: Radii.tag,
  },
  reservedChipOnDarkText: {
    fontFamily: Fonts.semiBold,
    fontSize: 12,
    lineHeight: 14,
    color: Brand.bosgroen,
  },
  bandBack: {
    padding: Spacing.xs,
    marginLeft: -Spacing.xs,
    opacity: 0.85,
    ...(Platform.OS === 'web' && { cursor: 'pointer', transitionDuration: '150ms' } as any),
  },
  bandBackHovered: {
    opacity: 1,
  },

  // Messages
  messagesContainer: {
    flex: 1,
    backgroundColor: Colors.chatSurface,
  },
  messagesContent: {
    paddingVertical: Spacing.md,
    flexGrow: 1,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: Colors.textMedium,
    textAlign: 'center',
    lineHeight: 20,
  },

  // Composer
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: Spacing.md,
    gap: 10,
    backgroundColor: Colors.white,
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
  },
  inputContainerDesktop: {
    padding: Spacing.base,
    gap: Spacing.md,
  },
  messageInputContainer: {
    flex: 1,
    marginBottom: 0, // Remove default Input container margin
  },
  messageInput: {
    lineHeight: COMPOSER_LINE_HEIGHT,
    textAlignVertical: 'center',
  },
  sendButton: {
    borderRadius: Radii.control,
    backgroundColor: Colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },

  // Deleted item banner
  deletedItemBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Spacing.xs,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.base,
    backgroundColor: Colors.background,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  deletedItemText: {
    ...Typography.bodySmall,
    fontStyle: 'italic',
    color: Colors.textLight,
  },
});
